import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { serialize } from 'object-to-formdata';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import format from "date-fns/format";

import { useHttp } from 'hooks/http.hook';
import { useInitialDocument } from 'hooks/documents';
import { freightsValidateSchema } from 'helpers/freightFormConfig';
import { CARGO, CARGO_V2 } from 'endpointsConst';

import { FORMAT_TIME } from 'constants/time';
import Cargo from 'models/cargo';
import Loading from '../../../components/Loading';
import FreightsParameters from 'components/FreightComponents/FreightsParametres';
import FreightsRoutes from 'components/FreightComponents/FreightsRoutes';
import FreightsDate from 'components/FreightComponents/FreightsDate';
import CargoPayment from 'components/Forms/CargoPayment';
import CargoDocuments from 'components/Forms/CargoDocuments';
import FreightsTransport from 'components/FreightComponents/FreightsTransport';
import AddCompanyInfo from 'components/Forms/AddCompanyInfoWithEmployee';
import ShadowBlock from 'components/common/Layout/ShadowBlock';
import Button from 'components/common/Button';
import Error from 'components/Forms/Error';

import styles from './styles.module.scss';
import { convertResponseToAddress } from '@/types/mapbox';
import { is24hoursTimeGap } from 'helpers/date';

// TODO refactor - copy of AddFreight
export default function EditFreight() {
  const { t } = useTranslation();

  const [initialValues, setInitialValues] = useState(Cargo.initialValues());

  const { request, loading } = useHttp();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useSelector((state) => state.settings.lang);
  const { fetchDocumnets } = useInitialDocument();

  const fetchData = useCallback(async () => {
    const id = location.search.split('=')[1];
    Promise.all([
      request(`${CARGO_V2}/exchange/${id}?lang=${lang}`, 'get'),
      request(`${CARGO_V2}/exchange/${id}/contacts`, 'get'),
    ]).then(async (res) => {
      const data = res[0].data;

      const loadingFrom = new Date(data.loading_from);
      const loadingTo = new Date(data.loading_to);
      const unloadingFrom = new Date(data.unloading_from)
      const unloadingTo = new Date(data.unloading_to);

      const convertedData = {
        ...data,
        loading_address: convertResponseToAddress(data.loading_address),
        unloading_address: convertResponseToAddress(data.unloading_address),
        additional_loading_addresses: data.additional_loading_addresses?.length
          ? data.additional_loading_addresses.map((item) => convertResponseToAddress(item))
          : [],
        additional_unloading_addresses: data.additional_unloading_addresses?.length
          ? data.additional_unloading_addresses.map((item) => convertResponseToAddress(item))
          : [],
        loading_date_from: loadingFrom.toISOString(),
        loading_date_to: loadingTo.toISOString(),
        loading_time_from: format(loadingFrom, FORMAT_TIME),
        loading_time_to: format(loadingTo, FORMAT_TIME),
        unloading_date_from: unloadingFrom.toISOString(),
        unloading_date_to: unloadingTo.toISOString(),
        unloading_time_from: format(unloadingFrom, FORMAT_TIME),
        unloading_time_to: format(unloadingTo, FORMAT_TIME),
        round_clock1: is24hoursTimeGap(loadingFrom, loadingTo),
        round_clock2: is24hoursTimeGap(unloadingFrom, unloadingTo),
        transport_bodytype_ids: data.bodytypes?.map((item) => item.id) || [],
      };

      const cargo = new Cargo(convertedData);

      if (cargo.documents) {
        try {
          // we send this file on backend, that's why we need to download them
          cargo.document = await fetchDocumnets(cargo.documents);
        } catch (e) {
          // TODO it couldn't be, but could
        }
      }

      setInitialValues({
        ...cargo,
        ...collectContactsFormData(res[1].data),
      });
    });
  }, [fetchDocumnets, lang, location.search, request]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const collectContactsFormData = (data) => {
    return { employee_ids: data.employees };
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={freightsValidateSchema(t)}
        onSubmit={(values, { setErrors }) => {
          const cargo = Cargo.formData(values);
          const formData = serialize(cargo, { indices: true });

          values.document &&
            values.document.forEach((el) => {
              formData.append('document', el);
            });
          request(`${CARGO}/exchange/${location.search.split('=')[1]}`, 'patch', formData)
            .then(() => {
              navigate('/freight/search');
            })
            .catch((e) => {
              const errors = {};
              e.response.data.errors.forEach((error) => {
                errors[error.path[0]] = t(error.type);
              });
              setErrors(errors);
            });
        }}
      >
        {({ errors, submitCount }) => (
          <Form>
            <ShadowBlock>
              <FreightsParameters />
              <FreightsRoutes />
              <FreightsTransport />
              <FreightsDate />
              <CargoPayment />
              <CargoDocuments />
            </ShadowBlock>
            <AddCompanyInfo />
            <div className={styles.form__footer}>
              {submitCount > 0 && Object.entries(errors, t).length > 0 && <Error>{t('errors.mainFormError')}</Error>}
              {loading && (
                <div className={styles.loading}>
                  <Loading />
                </div>
              )}
              <div className={styles.button__continue}>
                <Button disabled={loading} type="submit">
                  {t('edit_save')}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
